import { FrameData } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';
import { LayerRenderer } from './LayerRenderer';

export class GlowOrbRenderer implements LayerRenderer {
  private beatPulse = new BeatPulse(0.92);
  private smoothSize = new SmoothedValue(0.1);
  private smoothHue = new SmoothedValue(0.04);
  private orbitAngle = 0;

  init(): void {}

  render(
    ctx: CanvasRenderingContext2D,
    frame: FrameData,
    settings: Record<string, number>,
    width: number,
    height: number,
  ): void {
    const { bassEnergy, midEnergy, trebleEnergy, isBeat, beatIntensity, deltaTime } = frame;
    const baseSize = settings.baseSize ?? 0.15;
    const colorHue = settings.colorHue ?? 280;
    const orbitSpeed = settings.orbitSpeed ?? 0.5;
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);

    this.beatPulse.update(isBeat, beatIntensity);

    const size = this.smoothSize.update(
      baseSize + bassEnergy * 0.08 + this.beatPulse.value * 0.06
    );
    const orbRadius = size * minDim;
    const hue = this.smoothHue.update(colorHue + midEnergy * 40 - trebleEnergy * 30);

    // Outer glow
    const outerGlow = ctx.createRadialGradient(cx, cy, orbRadius * 0.5, cx, cy, orbRadius * 2.5);
    outerGlow.addColorStop(0, hsl(hue, 70, 50, 0.15 + this.beatPulse.value * 0.1));
    outerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = outerGlow;
    ctx.fillRect(0, 0, width, height);

    // Inner orb
    const innerGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, orbRadius);
    innerGlow.addColorStop(0, hsl(hue, 80, 70, 0.9));
    innerGlow.addColorStop(0.5, hsl(hue, 70, 55, 0.6));
    innerGlow.addColorStop(1, hsl(hue, 60, 40, 0));
    ctx.fillStyle = innerGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, orbRadius, 0, Math.PI * 2);
    ctx.fill();

    // Orbiting dots
    this.orbitAngle += (orbitSpeed * deltaTime) / 1000;
    const dotCount = 8;
    const orbitR = orbRadius * 1.4;
    for (let i = 0; i < dotCount; i++) {
      const angle = this.orbitAngle + (i / dotCount) * Math.PI * 2;
      const dx = cx + Math.cos(angle) * orbitR;
      const dy = cy + Math.sin(angle) * orbitR;
      const dotSize = 2 + midEnergy * 3;

      ctx.beginPath();
      ctx.arc(dx, dy, dotSize, 0, Math.PI * 2);
      ctx.fillStyle = hsl(hue + i * 30, 70, 65, 0.6 + this.beatPulse.value * 0.3);
      ctx.fill();
    }
  }

  resize(): void {}
  destroy(): void {}
}
